"use client";

import { useEffect } from "react";
import type { Metadata } from "next";
import { getMetadataFromOrigin } from "@/lib/utils/metadata";

const upsertLink = (rel: string, href: string): void => {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }

  link.href = href;
};

const upsertMetaDescription = (content: string): void => {
  let meta = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');

  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.appendChild(meta);
  }

  meta.content = content;
};

const getPrimaryIconUrl = (metadata: Metadata): string | undefined => {
  const icons = metadata.icons;

  if (!icons || typeof icons !== "object" || !("icon" in icons)) {
    return undefined;
  }

  const iconValue = (icons as { icon?: unknown }).icon;

  if (Array.isArray(iconValue)) {
    const firstIcon = iconValue[0];
    if (typeof firstIcon === "string") {
      return firstIcon;
    }

    if (firstIcon && typeof firstIcon === "object" && "url" in firstIcon) {
      return String((firstIcon as { url: string | URL }).url);
    }

    return undefined;
  }

  if (typeof iconValue === "string") {
    return iconValue;
  }

  if (iconValue && typeof iconValue === "object" && "url" in iconValue) {
    return String((iconValue as { url: string | URL }).url);
  }

  return undefined;
};

export default function ClientMetadata(): null {
  useEffect(() => {
    const origin = globalThis.location.origin;
    const metadata = getMetadataFromOrigin(origin);

    if (metadata.title) {
      document.title = String(metadata.title);
    }

    if (typeof metadata.description === "string") {
      upsertMetaDescription(metadata.description);
    }

    const iconUrl = getPrimaryIconUrl(metadata);

    if (typeof iconUrl === "string") {
      upsertLink("icon", iconUrl);
      upsertLink("shortcut icon", iconUrl);
      upsertLink("apple-touch-icon", iconUrl);
    }
  }, []);

  return null;
}
